'use client'

import React, {ReactElement, ReactNode, useEffect, useState} from 'react'
import classnames from 'classnames'

import {ModalsProvider} from '../contexts/ModalsContext'
import {Button, LinkButton} from '../ui/components'
import {BeforeRegModal} from '../ui/components/BeforeRegModal'
import {reachGoal} from '../utils/reachGoal'
import {AnchorLinks} from './components/AnchorLinks'
import {Expanded} from './components/Expanded'
import {HeaderBase, THeaderBaseRender, TIsMenuOpen} from './components/HeaderBase'
import {Logo} from './components/Logo'
import {Switcher} from './components/Switcher'
import {Context} from './Context'
import {TNavigation} from './types'

import styles from './styles.module.css'
import '../style/global.css'
import '../style/palette.css'
import '../style/sizes.css'

export type THeaderProps = {
  logoLink?: string
  logoAlt?: string
  navigationType?: 'switcher' | 'anchorLinks'
  navigation?: TNavigation[]
  cta?: {
    text: string
    link: string
    variant?: 'primary' | 'secondary' | 'navigation'
    goal?: string
  }
  metrikaId?: string
  showActionButton?: boolean
  showMenu?: boolean
  currentLink?: string
  showInMenuOnly?: boolean
  modals?: ReactElement[]
  sideSlot?: ReactNode | any
  isFixed?: boolean
}

export function Header({
  logoLink = 'https://dostavka.yandex.ru/',
  logoAlt = 'Яндекс Доставка',
  navigation = [],
  navigationType,
  currentLink,
  cta,
  metrikaId,
  showInMenuOnly = false,
  showActionButton = true,
  showMenu = true,
  modals,
  sideSlot,
  isFixed = false,
}: THeaderProps) {
  const currentLinkIndex = navigation.findIndex(({link}) => link === currentLink)
  const activeLinkIndex = currentLinkIndex === -1 ? 0 : currentLinkIndex
  const [menuSwitcherIndex, setMenuSwitcherIndex] = useState(activeLinkIndex)
  const hasMenu = navigation.length > 0

  const getLogo: THeaderBaseRender = () => <Logo href={logoLink} alt={logoAlt} />

  const getMain: THeaderBaseRender = ({isMenuOpen}) => {
    let main = null

    if (navigationType === 'anchorLinks') {
      main = <AnchorLinks navigation={navigation} currentLink={currentLink} />
    }

    if (navigationType === 'switcher') {
      main = (
        <Switcher
          type={isMenuOpen ? 'button' : 'link'}
          navigation={navigation}
          activeLinkIndex={isMenuOpen ? menuSwitcherIndex : activeLinkIndex}
          onChangeTab={setMenuSwitcherIndex}
        />
      )
    }

    return main
  }

  const getRight: THeaderBaseRender = ({isMenuOpen, setIsMenuOpen}) => {
    const action =
      navigationType === 'switcher' && navigation[menuSwitcherIndex]?.cta?.text
        ? navigation[menuSwitcherIndex].cta
        : cta

    return (
      <>
        {showActionButton && action && (
          <LinkButton
            size="l"
            variant={action?.variant || 'secondary'}
            href={action.link}
            onClick={() => {
              setIsMenuOpen(false)
              action?.goal && metrikaId && reachGoal?.(metrikaId, action.goal)
            }}
            className={styles.actionButton}
          >
            {action.text}
          </LinkButton>
        )}

        {sideSlot && <div className={styles.sideSlot}>{sideSlot}</div>}

        {showMenu && hasMenu && (
          <Button
            icon={isMenuOpen ? 'close' : 'menu'}
            iconSize="m"
            variant="navigation"
            onClick={() => {
              setIsMenuOpen(!isMenuOpen)
              setMenuSwitcherIndex(activeLinkIndex)
            }}
            className={styles.menuButton}
          />
        )}
      </>
    )
  }

  const getMenu: THeaderBaseRender = () => {
    if (showMenu && hasMenu) {
      return (
        <Expanded
          navigation={navigation}
          activeLinkIndex={menuSwitcherIndex}
          setMenuSwitcherIndex={setMenuSwitcherIndex}
        />
      )
    }

    return null
  }

  const useGetClassnames = ({isMenuOpen}: {isMenuOpen: TIsMenuOpen}) => {
    const [isHeaderStuck, setIsHeaderStuck] = useState(false)

    useEffect(() => {
      const handleScroll = () => {
        setIsHeaderStuck(window.scrollY >= 4)
      }

      window.addEventListener('scroll', handleScroll)
      window.addEventListener('resize', handleScroll)

      return () => {
        window.removeEventListener('resize', handleScroll)
        window.removeEventListener('scroll', handleScroll)
      }
    }, [])

    return {
      headerCN: isMenuOpen
        ? classnames(styles.header, styles.headerExpanded, {
            [styles.fixed]: isFixed,
            [styles.headerStuck]: isHeaderStuck,
          })
        : classnames(styles.header, {[styles.fixed]: isFixed, [styles.headerStuck]: isHeaderStuck}),
      mainCN: classnames(styles.mainContainer, {[styles.showInMenuOnly]: showInMenuOnly}),
      menuCN: styles.menuContainer,
      openMenuCN: styles.isMenuOpen,
      rightCN: styles.rightSection,
    }
  }

  return (
    <ModalsProvider>
      <Context.Provider value={{reachGoal: (goal: string) => metrikaId && reachGoal?.(metrikaId, goal)}}>
        {modals && modals.map(modal => <BeforeRegModal key={modal.props.id} {...modal.props} />)}
        <HeaderBase logo={getLogo} main={getMain} right={getRight} menu={getMenu} getClassnames={useGetClassnames} />
        {navigationType === 'switcher' && (
          <div className={classnames(styles.mobileSwitcher, {[styles.showInMenuOnly]: showInMenuOnly})}>
            <Switcher type="link" navigation={navigation} activeLinkIndex={activeLinkIndex} />
          </div>
        )}
      </Context.Provider>
    </ModalsProvider>
  )
}
